/** Order for card vs bank methods on the invoice page (matches Stripe snake_case types). */
export const CHECKOUT_METHOD_ORDER = ['card', 'us_bank_account', 'acss_debit'];

const BANK_PAYMENT_METHOD_TYPES = new Set(['acss_debit', 'us_bank_account']);

/**
 * Methods the invoice page offers, whatever else the PaymentIntent allows. Bank debit
 * (acss_debit / us_bank_account) and Link are turned off for now. Apple Pay and
 * Google Pay are card payments, so they stay available.
 *
 * Card-only shows the single-line CardElement. Adding a bank type here switches the
 * card form to the Payment Element (card + bank tabs), whose confirm path
 * (confirmDeferredPayment) has only been tested with cards. Before turning bank debit
 * on, run test bank payments on staging: confirm, the mandate step, the "processing"
 * pending screen, and refreshing the page while processing.
 */
export const ENABLED_CHECKOUT_METHODS = ['card'];

/**
 * True when the PaymentIntent exposes both card and a bank rail (PAD/ACH).
 * Used so the entry button only says "Card or bank account" when both are enabled.
 */
export function hasCardAndBankPaymentMethods(paymentMethodTypes) {
	if (!Array.isArray(paymentMethodTypes) || paymentMethodTypes.length === 0) {
		return false;
	}
	const hasCard = paymentMethodTypes.includes('card');
	const hasBank = paymentMethodTypes.some((t) =>
		BANK_PAYMENT_METHOD_TYPES.has(t),
	);
	return hasCard && hasBank;
}

/**
 * Label for the main checkout entry button from Stripe `payment_method_types`.
 */
export function getCheckoutEntryButtonLabel(dict, paymentMethodTypes) {
	if (!dict) {
		return '';
	}
	if (hasCardAndBankPaymentMethods(paymentMethodTypes)) {
		return dict.choosePaymentMethod;
	}
	if (!Array.isArray(paymentMethodTypes) || paymentMethodTypes.length === 0) {
		return dict.creditCard;
	}
	const hasCard = paymentMethodTypes.includes('card');
	const hasAcss = paymentMethodTypes.includes('acss_debit');
	const hasUsBank = paymentMethodTypes.includes('us_bank_account');
	if (hasCard && !hasAcss && !hasUsBank) {
		return dict.creditCard;
	}
	if (hasAcss && !hasCard) {
		return dict.directDebitText;
	}
	if (hasUsBank && !hasCard) {
		return dict.usBankAchText;
	}
	return dict.choosePaymentMethod;
}

export function getOrderedCheckoutMethods(paymentMethodTypes) {
	if (!Array.isArray(paymentMethodTypes) || paymentMethodTypes.length === 0) {
		return ['card'];
	}
	const allowed = new Set(paymentMethodTypes);
	return CHECKOUT_METHOD_ORDER.filter((id) => allowed.has(id));
}
